// ===== World 16: Upside-down land – REVERSED GRAVITY + EVERYTHING TOGETHER ("The big machine") =====
// In Upside-down land things fall UP (level.gravity = -1). The gravity arrow flips the whole world, the flip zone
// reverses gravity while you are inside it, and the cloud on the floor swallows a fall. The second half mixes it all
// with earlier ideas: ramp, pad, seesaw, stone, paint, banana, button, rope and pulley.
// The hero is a little bat ball with its face upside down. The goal is the basket hanging from the ceiling.
import { defineWorld, definePart } from '../../core/registry';
import { C, DEG, clamp, rr, outline, selBox } from '../../core/draw-helpers';
import type { CorePartType, Ctx2D, GameBody, Gfx, Level, PhysicsApi, Reskin, SkinDef, SoundFn, Vec, WorldMeta } from '../../core/types';
import { scene } from './scene';
import { levels } from './levels';
import { strings } from './strings';
import { K } from './shared';

// ---------- constants ----------
const INK = C.ink;
/** Matter's gravity per step at gravity.y = 1 (scale 0.001 * 16.67^2 = 0.2778 px/step^2); the zone pushes 2.6x against it */
const G = 0.2778;

// ---------- drawing helpers ----------
// The bat ball: purple with small ears and wings – the face is upside down.
// Differs from draw-helpers drawBall: linear gradient, wings/ears, mirrored face without cheeks or scared look.
function drawBall(ctx: Ctx2D, b: GameBody, kind: string, t: number, g: Gfx) {
  const { x, y } = b.position, r = b.circleRadius!;
  ctx.save(); ctx.translate(x, y); ctx.rotate(b.roll || 0);
  const flap = Math.sin(t * 0.4) * 0.3;
  const body = kind === 'helper' ? ['#FFE3A0', '#F2A516'] : ['#B8A2F0', '#6A4FC0'];
  // wings
  for (const s of [-1, 1]) {
    ctx.save(); ctx.translate(s * r * 0.8, 0); ctx.rotate(s * flap);
    ctx.beginPath(); ctx.moveTo(0, -4); ctx.quadraticCurveTo(s * r * 0.9, -r * 0.6, s * r * 1.05, 0); ctx.quadraticCurveTo(s * r * 0.7, -r * 0.05, s * r * 0.55, r * 0.25); ctx.quadraticCurveTo(s * r * 0.3, 0, 0, 4); ctx.closePath();
    ctx.fillStyle = body[1]; ctx.fill(); outline(ctx, 2); ctx.restore();
  }
  ctx.beginPath(); ctx.arc(0, 0, r, 0, 7); ctx.fillStyle = g.lg(ctx, -r, r, body[0], body[1]); ctx.fill(); outline(ctx, 2.5);
  // ears (pointing down – the ball hangs upside down)
  for (const s of [-1, 1]) { ctx.beginPath(); ctx.moveTo(s * r * 0.25, r * 0.8); ctx.lineTo(s * r * 0.55, r * 1.35); ctx.lineTo(s * r * 0.7, r * 0.6); ctx.closePath(); ctx.fillStyle = body[1]; ctx.fill(); outline(ctx, 2); }
  // upside-down face: the mouth above the eyes
  ctx.save(); ctx.rotate(Math.PI);
  const v = b.velocity, lx = clamp(-v.x * 0.5, -2.5, 2.5), ly = clamp(-v.y * 0.5, -2.5, 2.5);
  for (const s of [-1, 1]) {
    ctx.beginPath(); ctx.arc(s * r * 0.34, -r * 0.12, r * 0.27, 0, 7); ctx.fillStyle = '#fff'; ctx.fill();
    ctx.beginPath(); ctx.arc(s * r * 0.34 + lx * 0.6, -r * 0.12 + ly * 0.6, r * 0.15, 0, 7); ctx.fillStyle = INK; ctx.fill();
  }
  ctx.strokeStyle = INK; ctx.lineWidth = 2; ctx.lineCap = 'round';
  if (b.sad) { ctx.beginPath(); ctx.arc(0, r * 0.62, r * 0.26, 1.2 * Math.PI, 1.8 * Math.PI); ctx.stroke(); }
  else { ctx.beginPath(); ctx.arc(0, r * 0.25, r * 0.28, 0.15 * Math.PI, 0.85 * Math.PI); ctx.stroke(); }
  ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.moveTo(-r * 0.12, r * 0.47); ctx.lineTo(-r * 0.05, r * 0.62); ctx.lineTo(r * 0.02, r * 0.47); ctx.fill();
  ctx.restore();
  ctx.restore();
  ctx.save(); ctx.translate(x, y); ctx.beginPath(); ctx.ellipse(-r * .35, -r * .45, r * .24, r * .14, -0.6, 0, 7); ctx.fillStyle = 'rgba(255,255,255,.6)'; ctx.fill(); ctx.restore();
}
// The basket hanging from the ceiling. down = true: the opening faces down (for levels where things fall up).
// Differs from draw-helpers basket: can be mirrored, has a rope and stars, smaller shadow, purple fill when full.
function basket(ctx: Ctx2D, x: number, y: number, w: number, down: boolean, full: number | boolean, g: Gfx) {
  ctx.save(); ctx.translate(x, y);
  if (down) ctx.scale(1, -1);
  // rope up to the ceiling (the upside-down basket sits right under the ceiling)
  if (!down) {
    ctx.strokeStyle = '#8A5A2B'; ctx.lineWidth = 3;
    for (const s of [-1, 1]) { ctx.beginPath(); ctx.moveTo(s * (w / 2 + 4), -58); ctx.lineTo(0, -150); ctx.stroke(); }
    ctx.beginPath(); ctx.moveTo(0, -150); ctx.lineTo(0, -y - 50); ctx.stroke();
  }
  g.shadowOn(ctx, 10, 5);
  ctx.beginPath(); ctx.moveTo(-w / 2 - 6, -58); ctx.lineTo(-w / 2 + 8, 6); ctx.quadraticCurveTo(0, 12, w / 2 - 8, 6); ctx.lineTo(w / 2 + 6, -58); ctx.closePath();
  ctx.fillStyle = g.lg(ctx, -60, 10, '#F5D08A', '#C98A3E'); ctx.fill(); g.shadowOff(ctx); outline(ctx, 3);
  ctx.save(); ctx.clip(); ctx.strokeStyle = 'rgba(140,80,30,.5)'; ctx.lineWidth = 3;
  for (let yy = -46; yy < 6; yy += 14) { ctx.beginPath(); ctx.moveTo(-w / 2 - 6, yy); ctx.lineTo(w / 2 + 6, yy); ctx.stroke(); }
  for (let xx = -w / 2; xx < w / 2; xx += 18) { ctx.beginPath(); ctx.moveTo(xx, -58); ctx.lineTo(xx + 4, 10); ctx.stroke(); }
  ctx.restore();
  ctx.beginPath(); ctx.ellipse(0, -58, w / 2 + 6, 9, 0, 0, 7); ctx.fillStyle = full ? '#B8A2F0' : '#A8682A'; ctx.fill(); outline(ctx, 2.5);
  // small stars in the basket
  ctx.fillStyle = '#FFE07A';
  for (const [sx, sy] of [[-w * .25, -30], [w * .2, -20]]) { ctx.beginPath(); for (let i = 0; i < 10; i++) { const a = i * Math.PI / 5, rr2 = i % 2 ? 3 : 7; ctx.lineTo(sx + Math.cos(a) * rr2, sy + Math.sin(a) * rr2); } ctx.fill(); }
  ctx.restore();
}
// The gravity arrow: a big arrow showing which way "down" is. k = 0..1 spin while flipping.
function bigArrow(ctx: Ctx2D, up: boolean, sel: boolean, k: number, g: Gfx) {
  ctx.save(); if (up) ctx.rotate(Math.PI);
  ctx.rotate(k * Math.PI);
  g.shadowOn(ctx, 8, 4);
  ctx.beginPath(); ctx.moveTo(-12, -30); ctx.lineTo(12, -30); ctx.lineTo(12, 4); ctx.lineTo(26, 4); ctx.lineTo(0, 32); ctx.lineTo(-26, 4); ctx.lineTo(-12, 4); ctx.closePath();
  ctx.fillStyle = g.lg(ctx, -30, 32, '#FFE07A', '#FF8A3D'); ctx.fill(); g.shadowOff(ctx); outline(ctx, 3);
  ctx.restore();
  ctx.beginPath(); ctx.arc(0, 0, 38, 0, 7); ctx.lineWidth = 4; ctx.strokeStyle = 'rgba(255,255,255,.85)'; ctx.setLineDash([6, 6]); ctx.stroke(); ctx.setLineDash([]);
  if (sel) { ctx.beginPath(); ctx.arc(0, 0, 44, 0, 7); ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.stroke(); }
}
// The flip zone: a purple field with arrows moving the opposite way
function zoneArt(ctx: Ctx2D, w: number, h: number, t: number, sel?: boolean) {
  rr(ctx, -w / 2, -h / 2, w, h, 18); ctx.fillStyle = 'rgba(201,182,242,.35)'; ctx.fill();
  ctx.setLineDash([8, 8]); ctx.lineDashOffset = t * 0.5; ctx.lineWidth = 3; ctx.strokeStyle = 'rgba(120,90,200,.9)'; ctx.stroke(); ctx.setLineDash([]);
  ctx.save(); rr(ctx, -w / 2, -h / 2, w, h, 18); ctx.clip();
  ctx.strokeStyle = 'rgba(255,255,255,.95)'; ctx.lineWidth = 4; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
  for (let i = 0; i < 3; i++) {
    const x = -w / 3 + i * w / 3, yy = h / 2 - ((t * 1.2 + i * 30) % (h + 30));
    ctx.beginPath(); ctx.moveTo(x - 9, yy + 8); ctx.lineTo(x, yy - 2); ctx.lineTo(x + 9, yy + 8); ctx.stroke();
  }
  ctx.restore();
  if (sel) selBox(ctx, -w / 2 - 6, -h / 2 - 6, w + 12, h + 12, 22);
}
// The cloud: soft, swallows the fall. sq = squash (0..1) right after a hit.
function cloud(ctx: Ctx2D, w: number, h: number, sq: number, sel?: boolean) {
  ctx.save(); ctx.scale(1 + sq * 0.12, 1 - sq * 0.25);
  ctx.beginPath();
  ctx.moveTo(-w / 2, h / 2);
  ctx.arc(-w / 2 + h * 0.45, 0, h * 0.5, Math.PI * 0.5, Math.PI * 1.4);
  ctx.arc(-w * 0.1, -h * 0.2, h * 0.6, Math.PI * 1.1, Math.PI * 1.8);
  ctx.arc(w * 0.22, -h * 0.1, h * 0.55, Math.PI * 1.2, Math.PI * 1.95);
  ctx.arc(w / 2 - h * 0.45, h * 0.05, h * 0.45, Math.PI * 1.5, Math.PI * 0.5);
  ctx.closePath();
  ctx.fillStyle = '#FFFFFF'; ctx.fill(); outline(ctx, 3);
  ctx.fillStyle = 'rgba(160,210,255,.45)'; rr(ctx, -w / 2 + 6, h / 2 - 10, w - 12, 8, 4); ctx.fill();
  for (const s of [-1, 1]) { ctx.beginPath(); ctx.arc(s * w * 0.12, -2, 2.6, 0, 7); ctx.fillStyle = INK; ctx.fill(); }
  ctx.strokeStyle = INK; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(0, 2, 5, 0.15 * Math.PI, 0.85 * Math.PI); ctx.stroke();
  ctx.restore();
  if (sel) selBox(ctx, -w / 2 - 8, -h / 2 - 12, w + 16, h + 20, 16);
}
// plank: an upside-down table (legs pointing up)
function table(ctx: Ctx2D, w: number, h: number, sel: boolean, g: Gfx) {
  ctx.strokeStyle = INK; ctx.lineWidth = 7; ctx.lineCap = 'round';
  for (const s of [-1, 1]) { ctx.beginPath(); ctx.moveTo(s * (w / 2 - 14), -h / 2); ctx.lineTo(s * (w / 2 - 10), -h / 2 - 18); ctx.stroke(); }
  ctx.strokeStyle = '#E8716B'; ctx.lineWidth = 4;
  for (const s of [-1, 1]) { ctx.beginPath(); ctx.moveTo(s * (w / 2 - 14), -h / 2); ctx.lineTo(s * (w / 2 - 10), -h / 2 - 18); ctx.stroke(); }
  // block only checks opts.outline for truthiness (the selection colour is fixed)
  g.block(ctx, 0, 0, w, h, 0, '#FFB3A8', '#E8716B', h / 2, { outline: sel ? '#FFC93C' : null });
  ctx.fillStyle = 'rgba(255,255,255,.5)'; for (let x = -w / 2 + 16; x < w / 2 - 10; x += 26) { ctx.beginPath(); ctx.arc(x, 2, 2.5, 0, 7); ctx.fill(); }
}
// pad: an upside-down umbrella (catches and throws)
function umbrella(ctx: Ctx2D, w: number, h: number, sel: boolean) {
  ctx.strokeStyle = INK; ctx.lineWidth = 4; ctx.lineCap = 'round';
  ctx.beginPath(); ctx.moveTo(0, -h / 2); ctx.lineTo(0, h / 2 + 18); ctx.arc(-6, h / 2 + 18, 6, 0, Math.PI); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(-w / 2, -h / 2); ctx.quadraticCurveTo(0, h / 2 + 10, w / 2, -h / 2); ctx.closePath();
  const cs = ['#FF5A5F', '#FFC93C', '#7FD3FF', '#37C16B'];
  ctx.save(); ctx.clip(); for (let i = 0; i < 4; i++) { ctx.fillStyle = cs[i]; ctx.fillRect(-w / 2 + i * w / 4, -h / 2 - 2, w / 4 + 1, h + 20); } ctx.restore();
  ctx.beginPath(); ctx.moveTo(-w / 2, -h / 2); ctx.quadraticCurveTo(0, h / 2 + 10, w / 2, -h / 2); ctx.closePath(); outline(ctx, 3);
  if (sel) selBox(ctx, -w / 2 - 6, -h / 2 - 8, w + 12, h + 36, 12);
}
// stone: an upside-down flower pot (heavy)
function pot(ctx: Ctx2D, a: number, sel: boolean) {
  ctx.save(); ctx.rotate(a || 0);
  ctx.beginPath(); ctx.moveTo(-18, 20); ctx.lineTo(18, 20); ctx.lineTo(24, -12); ctx.lineTo(-24, -12); ctx.closePath();
  ctx.fillStyle = '#E0864A'; ctx.fill(); outline(ctx, 3);
  rr(ctx, -27, -22, 54, 12, 4); ctx.fillStyle = '#C96A30'; ctx.fill(); outline(ctx, 2.5);
  // the flower grows downwards
  ctx.strokeStyle = '#37A34F'; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(0, 20); ctx.lineTo(0, 30); ctx.stroke();
  ctx.beginPath(); ctx.arc(0, 32, 5, 0, 7); ctx.fillStyle = '#FF8FB8'; ctx.fill();
  ctx.restore();
  if (sel) { ctx.beginPath(); ctx.arc(0, 0, 34, 0, 7); ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.stroke(); }
}

// ---------- skins ----------
const skins = {
  // grassy earth hanging from the ceiling / lying on the floor
  upsidedown_ground(ctx, b, g) {
    const w = b.dw!, h = b.dh!;
    g.block(ctx, b.position.x, b.position.y, w, h, b.angle, '#9FDB8A', '#5CB35A', Math.min(12, w / 2, h / 2));
    ctx.save(); ctx.translate(b.position.x, b.position.y); ctx.rotate(b.angle);
    ctx.fillStyle = '#B98B62'; rr(ctx, -w / 2 + 3, -h / 2 + Math.min(14, h * 0.45), w - 6, h - Math.min(14, h * 0.45) - 3, 8); ctx.fill();
    ctx.restore();
  },
  // cloud shelf (white and soft)
  upsidedown_cloud_shelf(ctx, b, g) {
    const w = b.dw!, h = b.dh!;
    g.block(ctx, b.position.x, b.position.y, w, h, b.angle, '#FFFFFF', '#CFE6FF', Math.min(14, w / 2, h / 2));
    ctx.save(); ctx.translate(b.position.x, b.position.y); ctx.rotate(b.angle);
    ctx.fillStyle = 'rgba(255,255,255,.9)';
    for (let x = -w / 2 + 18; x < w / 2 - 10; x += 36) { ctx.beginPath(); ctx.arc(x, -h / 2 + 2, 10, Math.PI, 0); ctx.fill(); }
    ctx.restore();
  },
  // furniture wood (upside-down cupboards and shelves)
  upsidedown_furniture(ctx, b, g) {
    const w = b.dw!, h = b.dh!;
    g.block(ctx, b.position.x, b.position.y, w, h, b.angle, '#C9B6F2', '#8E76D4', Math.min(10, w / 2, h / 2));
    ctx.save(); ctx.translate(b.position.x, b.position.y); ctx.rotate(b.angle);
    ctx.fillStyle = 'rgba(255,255,255,.55)';
    if (h > w * 1.5) for (let y = -h / 2 + 30; y < h / 2 - 10; y += 50) { ctx.beginPath(); ctx.arc(0, y, 3.5, 0, 7); ctx.fill(); }
    ctx.restore();
  },
} satisfies Record<string, SkinDef>;

// Goal art: the bowl becomes a hanging basket, the bell becomes an upside-down basket
const goal = {
  bowl(ctx: Ctx2D, gl: { x: number; y: number; w: number }, full: number, t: number, g: Gfx) { basket(ctx, gl.x, gl.y, gl.w, false, full, g); },
  front(ctx: Ctx2D, gl: { x: number; y: number; w: number }) { const { x, y, w } = gl; ctx.save(); ctx.beginPath(); ctx.ellipse(x, y - 58, w / 2 + 6, 9, 0, 0, Math.PI); ctx.lineWidth = 5; ctx.strokeStyle = '#F5D08A'; ctx.stroke(); ctx.restore(); },
  bell(ctx: Ctx2D, gl: { x: number; y: number; hitT?: number }, t: number, g: Gfx) {
    // upside-down basket: the opening faces down
    const hit = gl.hitT != null;
    ctx.save(); ctx.translate(0, Math.sin(t * 0.04) * 2);
    basket(ctx, gl.x, gl.y - 28, 108, true, hit, g);
    ctx.restore();
  },
};

// ---------- parts ----------
interface CloudHit { o: GameBody; n: Vec; v: Vec; vn: number }

const parts = {
  // Gravity arrow: when the ball touches it, gravity flips for the whole world
  upsidedown_arrow: definePart({
    rects: [{ x: 0, y: 0, w: 70, h: 70, circle: true }], circle: 35, rot: false, sensor: true,
    draw(ctx, pl, sel, g) { ctx.save(); ctx.translate(pl.x, pl.y); bigArrow(ctx, false, sel, 0, g); ctx.restore(); },
    icon(ctx, g) { ctx.save(); ctx.scale(0.8, 0.8); bigArrow(ctx, false, false, 0, g); ctx.restore(); },
    create(api, pl) {
      // last: cooldown of 50 steps so one touch flips only once; flipT drives the spin animation
      const st = { last: -99, flipT: -99 };
      return {
        layer: 'mid',
        step(api) {
          const S = api.S;
          for (const b of S.dyn) {
            if (b.isStatic || !api.isBall(b)) continue;
            if (Math.hypot(b.position.x - pl.x, b.position.y - pl.y) < 35 + (b.circleRadius || 16) && S.t - st.last > 50) {
              st.last = S.t; st.flipT = S.t;
              api.engine.gravity.y = -api.engine.gravity.y;
              api.sfx('upsidedown_flip', pl.x, pl.y, '#FFE07A', 10);
            }
          }
        },
        draw(ctx, t, g, S) {
          const k = clamp((S.t - st.flipT) / 20, 0, 1);
          ctx.save(); ctx.translate(pl.x, pl.y);
          // the arrow points the way gravity pulls
          const up = api.engine.gravity.y < 0;
          bigArrow(ctx, up, false, S.t - st.flipT < 20 ? (1 - k) : 0, g);
          ctx.restore();
        },
      };
    },
    intro(ctx, t, g, h) {
      ctx.fillStyle = '#9FDB8A'; ctx.fillRect(0, 240, 460, 40); ctx.fillRect(0, 0, 460, 30);
      const tt = t % 240, flip = tt > 70;
      ctx.save(); ctx.translate(230, 210); bigArrow(ctx, flip, false, 0, g); ctx.restore();
      let bx = 60 + Math.min(tt, 70) * 2.4, by = 222;
      if (flip) { bx = 228 + (tt - 70) * 0.6; by = Math.max(48, 222 - (tt - 70) * (tt - 70) * 0.03); }
      g.ball(ctx, h.fakeBall(bx, by, 2, flip ? -3 : 0, 16), 'hero', t);
    },
  }),
  // Flip zone: inside the field things fall the opposite way
  upsidedown_zone: definePart({
    rects: [{ x: 0, y: 0, w: 130, h: 150 }], rot: false, sensor: true,
    draw(ctx, pl, sel) { ctx.save(); ctx.translate(pl.x, pl.y); zoneArt(ctx, 130, 150, performance.now() / 16, sel); ctx.restore(); },
    icon(ctx) { ctx.save(); ctx.scale(0.55, 0.5); zoneArt(ctx, 130, 150, 0); ctx.restore(); },
    create(api, pl) {
      let whoosh = -99;
      return {
        layer: 'back',
        step(api) {
          const S = api.S, gy = api.engine.gravity.y;
          for (const b of S.dyn) {
            if (b.isStatic || b.kind === 'seesaw') continue;
            if (Math.abs(b.position.x - pl.x) > 65 || Math.abs(b.position.y - pl.y) > 75) continue;
            // 2.6x gravity the other way: cancels gravity and pulls 1.6x in the opposite direction
            api.Body.setVelocity(b, { x: b.velocity.x, y: b.velocity.y - 2.6 * G * gy });
            if (api.isBall(b) && S.t - whoosh > 40) { whoosh = S.t; api.sfx('upsidedown_swoop', b.position.x, b.position.y, '#C9B6F2', 5); }
          }
        },
        draw(ctx, t) { ctx.save(); ctx.translate(pl.x, pl.y); zoneArt(ctx, 130, 150, t, false); ctx.restore(); },
      };
    },
    intro(ctx, t, g, h) {
      ctx.fillStyle = '#9FDB8A'; ctx.fillRect(0, 240, 460, 40);
      ctx.save(); ctx.translate(250, 160); zoneArt(ctx, 130, 150, t, false); ctx.restore();
      const tt = t % 240;
      let bx = 40 + tt * 1.6, by = 224;
      if (bx > 185 && bx < 315) by = 224 - Math.sin((bx - 185) / 130 * Math.PI) * 140;
      g.ball(ctx, h.fakeBall(Math.min(bx, 440), by, 2, 0, 16), 'hero', t);
    },
  }),
  // Cloud: soft – swallows the fall so the ball stays put
  upsidedown_cloud: definePart({
    rects: [{ x: 0, y: 0, w: 110, h: 40 }], rot: 15, defaultA: 0,
    draw(ctx, pl, sel) { ctx.save(); ctx.translate(pl.x, pl.y); ctx.rotate((pl.a || 0) * DEG); cloud(ctx, 110, 40, 0, sel); ctx.restore(); },
    icon(ctx) { ctx.save(); ctx.scale(0.75, 0.75); cloud(ctx, 110, 40, 0); ctx.restore(); },
    create(api, pl) {
      const a = (pl.a || 0) * DEG, nx = -Math.sin(a), ny = Math.cos(a);
      const b = api.add(api.R(pl.x, pl.y, 110, 40, { isStatic: true, angle: a, friction: 0.9, restitution: 0, chamfer: { radius: 16 }, label: 'upsidedown_cloud' }));
      b.kind = 'upsidedown_cloud';
      // hits are collected in onHit and damped after the step: the normal speed is removed, the tangential speed kept at 20 %
      const todo: CloudHit[] = []; let hitT = -99;
      api.onHit(b, o => {
        if (!api.isDynamic(o)) return;
        const s = (o.position.x - pl.x) * nx + (o.position.y - pl.y) * ny >= 0 ? 1 : -1;
        const vn = o.velocity.x * nx * s + o.velocity.y * ny * s;
        if (vn < -0.5) todo.push({ o, n: { x: nx * s, y: ny * s }, v: { x: o.velocity.x, y: o.velocity.y }, vn });
      });
      return {
        layer: 'mid',
        after(api) {
          for (const q of todo.splice(0)) {
            const vt = { x: q.v.x - q.vn * q.n.x, y: q.v.y - q.vn * q.n.y };
            api.Body.setVelocity(q.o, { x: vt.x * 0.2, y: vt.y * 0.2 });
            hitT = api.S.t; api.sfx('upsidedown_puff', q.o.position.x, q.o.position.y, '#FFFFFF', 6);
          }
        },
        draw(ctx, t, g, S) { const k = S.t - hitT, sq = k < 20 ? Math.sin(k / 20 * Math.PI) : 0; ctx.save(); ctx.translate(pl.x, pl.y); ctx.rotate(a); cloud(ctx, 110, 40, sq, false); ctx.restore(); },
      };
    },
    intro(ctx, t, g, h) {
      ctx.fillStyle = '#9FDB8A'; ctx.fillRect(0, 0, 460, 30);
      // the ball falls UP and lands softly in the cloud under the ceiling
      const tt = t % 200, y = Math.max(76, 230 - tt * tt * 0.02), sq = y <= 76 && tt < 110 ? 0.6 : 0;
      ctx.save(); ctx.translate(230, 50); ctx.scale(1, -1); cloud(ctx, 120, 40, sq, false); ctx.restore();
      g.ball(ctx, h.fakeBall(230, y, 0, -3, 16), 'hero', t);
    },
  }),
};

// ---------- objects ----------
// No own objects; setup gives the upside-down basket (bell goal with basket: true) a solid bottom.
function setup(api: PhysicsApi, level: Level) {
  const gl = level.goal;
  // the basket hanging with its opening down gets a bottom, so the ball can "fall" up into it
  if (gl && gl.type === 'bell' && gl.basket) {
    const { R } = api, x = gl.x, y = gl.y, w = 120;
    // only the bottom is solid; the sides are soft nets the ball can slide in under
    const bodies = [R(x, y - 34, w, 12, { isStatic: true, chamfer: { radius: 5 } })];
    bodies.forEach(p => { p.kind = 'upsidedown_basket'; p.friction = 0.2; p.restitution = 0.05; api.add(p); });
  }
}

// ---------- sounds ----------
const sounds: Record<string, SoundFn> = {
  upsidedown_flip: (tone) => { tone(300, 0.3, 'sine', 0.18, 900); tone(900, 0.3, 'sine', 0.1, 300, 0.15); },
  upsidedown_puff: (tone, noise) => { noise(0.3, 0.15, 600, 0.6); },
  upsidedown_swoop: (tone) => { tone(500, 0.2, 'triangle', 0.12, 1000); },
};

// ---------- reskins ----------
const reskin = {
  plank(ctx, pl, sel, g, rects) { const r = rects[0]; ctx.save(); ctx.translate(r.x, r.y); ctx.rotate(r.a); table(ctx, r.w, r.h, sel, g); ctx.restore(); },
  long_plank(ctx, pl, sel, g, rects) { const r = rects[0]; ctx.save(); ctx.translate(r.x, r.y); ctx.rotate(r.a); table(ctx, r.w, r.h, sel, g); ctx.restore(); },
  pad(ctx, pl, sel, g, rects) { const r = rects[0]; ctx.save(); ctx.translate(r.x, r.y); ctx.rotate(r.a); umbrella(ctx, r.w, r.h, sel); ctx.restore(); },
  stone(ctx, pl, sel) { const body = pl.body as GameBody | undefined; ctx.save(); ctx.translate(pl.x, pl.y); pot(ctx, body ? body.angle : 0, sel); ctx.restore(); },
  seesaw(ctx, pl, sel, g) {
    const x = pl.x, y = pl.y;
    ctx.save(); g.shadowOn(ctx, 8, 4);
    ctx.beginPath(); ctx.moveTo(x, y + 2); ctx.lineTo(x - 24, y + 40); ctx.lineTo(x + 24, y + 40); ctx.closePath();
    ctx.fillStyle = g.lg(ctx, y, y + 40, '#9FDB8A', '#5CB35A'); ctx.fill(); g.shadowOff(ctx); outline(ctx, 3); ctx.restore();
    g.block(ctx, x, y, 230, 14, (pl.a || 0) * DEG, '#FFE07A', '#F2A516', 7);
    ctx.beginPath(); ctx.arc(x, y, 7, 0, 7); ctx.fillStyle = '#fff'; ctx.fill(); outline(ctx, 3);
  },
  banana(ctx, pl, sel, g, rects) {
    // an upside-down boat (arc)
    ctx.save(); ctx.lineCap = 'round';
    ctx.beginPath(); rects.forEach((r, i) => { const px = r.x - Math.sin(r.a) * -4, py = r.y + Math.cos(r.a) * -4; i ? ctx.lineTo(px, py) : ctx.moveTo(px, py); });
    ctx.lineWidth = 24; ctx.strokeStyle = INK; ctx.stroke(); ctx.lineWidth = 18; ctx.strokeStyle = sel ? '#FFE07A' : '#7FD3FF'; ctx.stroke();
    ctx.lineWidth = 4; ctx.strokeStyle = '#FFFFFF'; ctx.stroke(); ctx.restore();
  },
  paint(ctx, pl, sel) {
    // a blob of honey dripping upwards (sticky – slows down)
    ctx.save(); ctx.translate(pl.x, pl.y);
    ctx.fillStyle = '#FFC93C'; ctx.globalAlpha = 0.9;
    ctx.beginPath(); ctx.ellipse(0, 6, 40, 16, 0, 0, 7); ctx.fill();
    for (const [x, h] of [[-20, 22], [4, 30], [24, 18]]) { ctx.beginPath(); ctx.ellipse(x, 6 - h / 2, 6, h / 2, 0, 0, 7); ctx.fill(); }
    ctx.globalAlpha = 1; ctx.beginPath(); ctx.ellipse(0, 6, 40, 16, 0, 0, 7); outline(ctx, 2.5);
    if (sel) selBox(ctx, -46, -32, 92, 64, 14);
    ctx.restore();
  },
} satisfies Partial<Record<CorePartType, Reskin>>;

const icons = {
  plank(ctx, g) { ctx.save(); ctx.rotate(-0.3); table(ctx, 100, 16, false, g); ctx.restore(); },
  pad(ctx) { ctx.save(); ctx.translate(0, -10); umbrella(ctx, 80, 18, false); ctx.restore(); },
  stone(ctx) { pot(ctx, 0, false); },
} satisfies Partial<Record<CorePartType, (ctx: Ctx2D, gfx: Gfx) => void>>;

// ---------- meta ----------
const meta: WorldMeta = {
  icon: '🙃', strings, music: 'upsidedown.mp3',
  credits: { title: 'Enchanted Fantasy', artist: 'Sonican', id: 356791, url: 'https://pixabay.com/music/fantasy-dreamy-childrens-enchanted-fantasy-356791/' },
  synth: { bpm: 66, root: 261.6, sc: 'lydian', prog: [0, 1, 5, 4], bar: 3, lead: 'bell', dens: 0.35 },
};

// ---------- world ----------
export default defineWorld({
  k: K,
  meta,
  scene,
  wash: 'rgba(250,252,255,.5)',
  setup,
  skins,
  ball: drawBall,
  goal,
  parts,
  sounds,
  reskin,
  icons,
  levels,
});
